import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { useNavigationItems } from '@src/hooks/useNavigationItems';
import { useDisplaySettings } from '@src/providers/DisplaySettingsProvider';
import { Divider, MixpanelEventSource, MixpanelSourceContext, Tour, type BoxProps } from '@talos/kyoko';
import { useCallback, useEffect, useRef, useState } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import { SIDEBAR_MENU_WIDTH_NARROW, SIDEBAR_MENU_WIDTH_WIDE } from '../../styles/dimensions';
import { ShowcaseName } from '../../tokens/showcase';
import { TourName } from '../../tokens/tours';
import { envConfig } from '../../utils/envConfig';
import { StackedLogo } from '../Logo/StackedLogo';
import { ShowcaseTrigger } from '../Showcase';
import { NavigationSidebarSubMenu } from './NavigationSidebarSubMenu';
import { SidebarConnectionsItem } from './SidebarConnectionsItem';
import { SidebarHelpItem } from './SidebarHelpItem';
import { SidebarNavItem } from './SidebarNavItem';
import { SidebarReleaseNotesItem } from './SidebarReleaseNotesItem';
import { SidebarSearchItem } from './SidebarSearchItem';
import { SidebarUserProfileItem } from './SidebarUserProfileItem';
import { SidebarLogoWrapper, SidebarMenu, SidebarMenuBottomItems, SidebarMenuTopItems, SidebarWrapper } from './styles';

export const NavigationSidebar = (props: BoxProps) => {
  const { pinNavigationSidebar, showSidebarLabels } = useDisplaySettings();
  const { showConnectionStatusTooltip } = useFeatureFlag();
  const { navItems, topNavItems, bottomNavItems } = useNavigationItems();
  const location = useLocation();
  const routes = matchRoutes(navItems, location);
  const topLevelMatch = routes?.at(0);
  const groupMatch = routes?.[1];
  const hasSubNavItems = !!topLevelMatch?.route?.children?.length;
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSubMenu, setShowSubMenu] = useState(false);
  const isPinned = pinNavigationSidebar;
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const handleMouseEnter = useCallback(() => {
    setIsExpanded(true);
    setShowSubMenu(true);
    timerRef.current && clearTimeout(timerRef.current);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsExpanded(false);
    timerRef.current = setTimeout(() => {
      setShowSubMenu(false);
    }, 200);
  }, []);

  useEffect(() => {
    return () => {
      timerRef.current && clearTimeout(timerRef.current);
    };
  }, []);

  return (
    <MixpanelSourceContext.Provider value={MixpanelEventSource.NavigationSidebar}>
      <ShowcaseTrigger showcase={ShowcaseName.v255UpdatedNavigationSidebar} />
      <SidebarWrapper
        data-testid="sidebar-wrapper"
        {...props}
        hasSubNavItems={hasSubNavItems}
        isPinned={isPinned}
        isExpanded={isExpanded}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        wide={showSidebarLabels}
      >
        <SidebarMenu
          data-testid="sidebar-menu"
          w={showSidebarLabels ? SIDEBAR_MENU_WIDTH_WIDE : SIDEBAR_MENU_WIDTH_NARROW}
          pb={showSidebarLabels ? 'spacing16' : 'spacing12'}
        >
          <SidebarLogoWrapper to="/">
            <StackedLogo showLabel={showSidebarLabels} />
          </SidebarLogoWrapper>
          <SidebarMenuTopItems
            mt={showSidebarLabels ? 'spacing16' : 'spacing12'}
            gap={showSidebarLabels ? 'spacing12' : 'spacing8'}
          >
            {topNavItems.map(item => (
              <SidebarNavItem key={item.path} item={item} />
            ))}
          </SidebarMenuTopItems>
          <SidebarMenuBottomItems>
            {showConnectionStatusTooltip && <SidebarConnectionsItem showLabel={showSidebarLabels} />}
            <Divider mx="spacing8" />
            <SidebarSearchItem />
            <SidebarHelpItem setExpanded={setIsExpanded} />
            <Tour tour={TourName.v255AccessShowcaseFromWhatsNew} />
            <SidebarReleaseNotesItem />
            {bottomNavItems.map(item => (
              <SidebarNavItem key={item.path} item={item} />
            ))}
            <SidebarUserProfileItem env={envConfig.VITE_AVA_ENV} uiVersion={import.meta.env.VITE_NPM_PACKAGE_VERSION} />
          </SidebarMenuBottomItems>
        </SidebarMenu>

        <NavigationSidebarSubMenu
          visible={showSubMenu || isPinned}
          wrapperExpanded={hasSubNavItems && isPinned}
          isExpanded={isExpanded}
          hasSubNavItems={hasSubNavItems}
          topLevelMatch={topLevelMatch}
          groupMatch={groupMatch}
        />
      </SidebarWrapper>
    </MixpanelSourceContext.Provider>
  );
};
